import React, { useState, useEffect } from 'react';
// types
import type { WCardTemplatePageView } from '@zola/svc-web-api-ts-client';
import type { CustomTemplateSummary } from 'cards/components/common/SingleSample/util/types';
import { CARD_TYPES } from 'cards/constants/CardTypes';

// components
import SamplePreview, { SamplePreviewProps } from 'paper/components/SamplePreview/SamplePreview';
import ProductColorPicker, {
  ProductColorPickerProps,
} from 'cards/components/common/ProductColorPicker/ProductColorPicker';
// styles
import styles from './SamplePreviewCustom.module.less';

export interface SamplePreviewCustomProps {
  customPreviewTemplates?: CustomTemplateSummary[];
  customPhotoUrl?: string;
  /** onClick handler for when preview or 'More' clicked */
  onClick: () => void;
  /** Styling for container */
  className?: string;
  cardType?: string;
  samplePreviewPropOverrides?: Pick<SamplePreviewProps, 'className' | 'templateSize'>;
  productColorPickerPropOverrides?: Pick<
    ProductColorPickerProps,
    'maxColorsToShow' | 'swatchWidth' | 'className'
  >;
  enableColorPicker?: boolean;
  enableFrame?: boolean;
}

const SamplePreviewCustom: React.FC<SamplePreviewCustomProps> = ({
  onClick,
  customPreviewTemplates,
  customPhotoUrl,
  className,
  samplePreviewPropOverrides,
  productColorPickerPropOverrides,
  enableColorPicker = true,
  enableFrame = true,
  children,
  cardType,
}) => {
  const [activeTemplate, setActiveTemplate] = useState<CustomTemplateSummary>();
  const isFolded = cardType === CARD_TYPES.thankyou;

  useEffect(() => {
    if (customPreviewTemplates) {
      setActiveTemplate(customPreviewTemplates[0]);
    }
  }, [customPreviewTemplates]);

  const onSelectColor = (t: CustomTemplateSummary): void => {
    setActiveTemplate(t);
  };

  const previewTemplate: WCardTemplatePageView | undefined = activeTemplate?.pages?.[0];

  if (!customPreviewTemplates) return null;
  if (!previewTemplate) return null;

  return (
    <div className={className}>
      <a className={styles.hoverCursor} onClick={onClick}>
        <SamplePreview
          customPreviewTemplates={customPreviewTemplates}
          templatePage={previewTemplate}
          isRounded={!isFolded}
          photoURL={customPhotoUrl}
          defaultCropType="cover"
          templateSize={samplePreviewPropOverrides?.templateSize}
          className={samplePreviewPropOverrides?.className}
          enableFrame={enableFrame}
          isFolded={isFolded}
        />
        {children}
      </a>
      {enableColorPicker && (
        <ProductColorPicker
          colors={customPreviewTemplates}
          activeColor={activeTemplate?.value}
          onSelect={onSelectColor}
          swatchWidth={productColorPickerPropOverrides?.swatchWidth || 28}
          maxColorsToShow={productColorPickerPropOverrides?.maxColorsToShow}
          disableMobileSwatchSizing
          onMoreClick={onClick}
          className={productColorPickerPropOverrides?.className}
        />
      )}
    </div>
  );
};

export default SamplePreviewCustom;
